import { useEffect, useMemo, useState } from "react";
import CommonModal from "../modalLayout/CommonModal";
import DistributorSelect from "../../va/DistributorSelect";
import { useGetVaDistributorsQuery } from "../../../app/vaApi/vaApi";
import {
  formatDistributorLabel,
  mapDistributorOption,
} from "../../../helpers/vaInvoiceUtils";
import "../../../styles/vaOrderModal.css";

function resolveSelectedId(options, { value, distributorId }) {
  if (!options.length) return "";

  const byId = options.find(
    (opt) => String(opt.id) === String(distributorId || "")
  );
  if (byId) return String(byId.id);

  const normalizedValue = String(value || "").trim().toLowerCase();
  if (!normalizedValue) return "";

  const byLabel = options.find((opt) => {
    const label = formatDistributorLabel(opt).toLowerCase();
    const name = String(opt.name || "").trim().toLowerCase();
    return label === normalizedValue || name === normalizedValue;
  });

  return byLabel ? String(byLabel.id) : "";
}

/**
 * Edit Order From using the same distributor dropdown as Create Order.
 * Saving OrderFrom through the parent update flow maintains ChangeLog.
 */
export default function EditOrderFromModal({
  show,
  setShow,
  value = "",
  distributorId = "",
  onSave,
  confirmLoading = false,
}) {
  const [selectedId, setSelectedId] = useState("");
  const [error, setError] = useState(false);

  const { data, isFetching } = useGetVaDistributorsQuery(
    { page: 1, limit: 100 },
    { skip: !show }
  );

  const options = useMemo(
    () => (data?.data?.items ?? []).map(mapDistributorOption).filter(Boolean),
    [data]
  );

  useEffect(() => {
    if (!show) return;
    setError(false);
    setSelectedId(resolveSelectedId(options, { value, distributorId }));
  }, [show, value, distributorId, options]);

  const handleConfirm = async () => {
    if (confirmLoading) return;
    if (!selectedId) {
      setError(true);
      return;
    }

    const selected = options.find(
      (opt) => String(opt.id) === String(selectedId)
    );
    if (!selected) {
      setError(true);
      return;
    }

    await onSave?.({
      orderFrom: formatDistributorLabel(selected),
      distributorId: selected.id,
      distributorName: selected.name || "",
    });
  };

  return (
    <CommonModal
      show={show}
      setShow={setShow}
      title="Edit order from"
      className="modal-md"
      centered
      onConfirm={handleConfirm}
      confirmLoading={confirmLoading}
      confirmDisabled={isFetching || confirmLoading}
      cancelDisabled={confirmLoading}
    >
      <DistributorSelect
        value={selectedId}
        onChange={(id) => {
          setSelectedId(id);
          setError(false);
        }}
        options={options}
        error={error}
        hideTitle
      />
      {isFetching ? (
        <p className="text-muted small mb-0 mt-2">Loading distributors…</p>
      ) : null}
      {error ? (
        <p className="text-danger small mb-0 mt-2">Select a distributor</p>
      ) : null}
    </CommonModal>
  );
}
